import _defineProperty from '@babel/runtime-corejs2/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime-corejs2/helpers/objectWithoutProperties';
import { forwardRef, useEffect, cloneElement } from 'react';
import PropTypes from 'prop-types';
import styledSystemPropTypes from '@styled-system/prop-types';
import styled, { keyframes } from 'styled-components';
import { compose, background, border, color, layout, shadow, space, typography, position } from 'styled-system';
import { Tooltip as Tooltip$1, TooltipArrow, useTooltipState, TooltipReference } from 'reakit/Tooltip';
import { theme, prop } from 'styled-tools';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';

const _excluded = ["cancelTooltip", "clickingTriggerBlinksTooltip", "onFirstRenderShowTooltip"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const blinkKeyframes = keyframes(["100%{opacity:1;}80%{opacity:.8;}60%{opacity:.6;}40%{opacity:.4;}20%{opacity:.2;}40%{opacity:.4;}80%{opacity:.8;}100%{opacity:1;}"]);
const StyledReakitToolTip = styled(Tooltip$1).withConfig({
  shouldForwardProp: prop => !['tooltipZIndex', 'onShowOpacityAnimation', 'onHideOpacityAnimation', 'onShowDelayDuration', 'onHideDelayDuration', 'backgroundColor', 'arrowColor', 'borderColor', 'zIndex'].includes(prop)
}).withConfig({
  displayName: "tooltip__StyledReakitToolTip",
  componentId: "wkjwyp-0"
})(["color:", ";line-height:18px;text-align:center;font-size:", ";z-index:", ";max-width:260px;background-color:", ";padding:8px;border:1px solid ", ";border-radius:", ";box-shadow:", ";font-family:", ";opacity:0;&[data-enter]{transition-property:opacity;transition-duration:", "ms;transition-timing-function:", ";transition-delay:", "ms;opacity:1;}&[data-leave]{transition-property:opacity;transition-duration:", "ms;transition-timing-function:", ";transition-delay:", "ms;}&.blink{animation:", " 300ms ease-in-out 1;}", ""], theme('colors.toolTipText'), theme('fontSizes.md'), prop('tooltipZIndex'), theme('colors.toolTipBackground'), theme('colors.toolTipBackground'), theme('radii.lg'), theme('shadows.xl'), theme('fonts.default'), prop('onShowOpacityAnimation.duration'), prop('onShowOpacityAnimation.timing'), prop('onShowDelayDuration'), prop('onHideOpacityAnimation.duration'), prop('onHideOpacityAnimation.timing'), prop('onHideDelayDuration'), blinkKeyframes, compose(background, border, color, layout, shadow, space, typography, position));
const StyledTooltipArrow = styled(TooltipArrow).withConfig({
  displayName: "tooltip__StyledTooltipArrow",
  componentId: "wkjwyp-1"
})(["fill:", ";stroke:transparent;border:none;& svg > .stroke{display:none;}"], prop('fill', theme('colors.toolTipBackground')));
const Tooltip = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      cancelTooltip,
      clickingTriggerBlinksTooltip,
      onFirstRenderShowTooltip
    } = props,
    omittedProps = _objectWithoutProperties(props, _excluded);

  // if no content, simply return the children
  if (!props.content) return props.children;
  const tooltipState = useTooltipState({
    placement: props.placement || 'left',
    gutter: props.gutter + 10 || 14,
    visible: onFirstRenderShowTooltip ? true : false,
    animated: true
  });

  // needed to fix position of tooltip when content prop changes
  useEffect(() => {
    tooltipState.unstable_update();
  }, [props.content]);
  useEffect(() => {
    if (onFirstRenderShowTooltip && onFirstRenderShowTooltip.triggerMouseoutAfterXSeconds && onFirstRenderShowTooltip.triggerMouseoutAfterXSeconds !== undefined) {
      setTimeout(() => {
        var evt = document.createEvent('MouseEvents');
        evt.initMouseEvent('mouseout', true, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null);
        tooltipState.unstable_referenceRef.current.dispatchEvent(evt);
      }, onFirstRenderShowTooltip.triggerMouseoutAfterXSeconds);
    }
  }, []);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("div", {
      style: {
        display: 'inline-flex',
        width: '100%'
      },
      onClick: () => {
        if (clickingTriggerBlinksTooltip === false || tooltipState.animating === true) {
          return false;
        }
        const tooltipInDOM = document.getElementById(tooltipState.baseId);
        tooltipInDOM.classList.add('blink');
        setTimeout(() => {
          tooltipInDOM.classList.remove('blink');
        }, 300);
      },
      children: typeof props.children === 'string' ?
      /*#__PURE__*/
      /* if tooltip trigger is just a string of text */
      jsx(TooltipReference, _objectSpread(_objectSpread({}, tooltipState), {}, {
        children: /*#__PURE__*/jsx("div", {
          style: {
            display: 'inline-block',
            width: '100%'
          },
          "data-cy": "TooltipTrigger",
          children: props.children
        })
      })) :
      /*#__PURE__*/
      /* if tooltip trigger is not a string of text i.e. text in the DOM */
      jsx(TooltipReference, _objectSpread(_objectSpread(_objectSpread({}, tooltipState), {}, {
        ref: props.children.ref
      }, props.children.props), {}, {
        children: referenceProps => {
          return /*#__PURE__*/jsx("div", {
            "data-cy": "TooltipTrigger",
            style: {
              display: 'inline-block',
              width: '100%'
            },
            children: /*#__PURE__*/cloneElement(props.children, referenceProps)
          });
        }
      }))
    }), cancelTooltip ? null : onFirstRenderShowTooltip.sticky ? /*#__PURE__*/jsxs(StyledReakitToolTip, _objectSpread(_objectSpread(_objectSpread({
      id: tooltipState.baseId // used by blink prop
    }, tooltipState), {}, {
      visible: true,
      ref: ref
    }, omittedProps), {}, {
      children: [/*#__PURE__*/jsx(StyledTooltipArrow, _objectSpread(_objectSpread({
        "data-cy": "TooltipArrow"
      }, tooltipState), {}, {
        size: "35px",
        fill: props.arrowColor
      })), props.content]
    })) : /*#__PURE__*/jsxs(StyledReakitToolTip, _objectSpread(_objectSpread(_objectSpread({
      id: tooltipState.baseId // used by blink prop
    }, tooltipState), {}, {
      ref: ref
    }, omittedProps), {}, {
      children: [/*#__PURE__*/jsx(StyledTooltipArrow, _objectSpread(_objectSpread({
        "data-cy": "TooltipArrow"
      }, tooltipState), {}, {
        size: "35px",
        fill: props.arrowColor
      })), props.content]
    }))]
  });
});
Tooltip.displayName = 'Tooltip';
Tooltip.defaultProps = {
  'data-cy': 'Tooltip',
  // component name
  tooltipZIndex: 20100,
  onHideDelayDuration: 200,
  onShowDelayDuration: 500,
  onShowOpacityAnimation: {
    duration: 200,
    timing: 'ease'
  },
  onHideOpacityAnimation: {
    duration: 200,
    timing: 'ease'
  },
  clickingTriggerBlinksTooltip: false,
  onFirstRenderShowTooltip: false
};
Tooltip.propTypes = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, styledSystemPropTypes.space), styledSystemPropTypes.color), styledSystemPropTypes.background), styledSystemPropTypes.border), styledSystemPropTypes.layout), styledSystemPropTypes.shadow), styledSystemPropTypes.typography), {}, {
  as: PropTypes.string,
  forwardAs: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.node]).isRequired,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.node]).isRequired,
  onFirstRenderShowTooltip: PropTypes.oneOfType([PropTypes.shape({
    triggerMouseoutAfterXSeconds: PropTypes.number,
    sticky: PropTypes.bool
  }), PropTypes.bool]),
  placement: PropTypes.oneOf(['auto-start', 'auto', 'auto-end', 'top-start', 'top', 'top-end', 'right-start', 'right', 'right-end', 'bottom-end', 'bottom', 'bottom-start', 'left-end', 'left', 'left-start']),
  gutter: PropTypes.number,
  tooltipZIndex: PropTypes.number,
  cancelTooltip: PropTypes.bool,
  onShowDelayDuration: PropTypes.number,
  onHideDelayDuration: PropTypes.number,
  onShowOpacityAnimation: PropTypes.shape({
    duration: PropTypes.number,
    timing: PropTypes.string
  }),
  onHideOpacityAnimation: PropTypes.shape({
    duration: PropTypes.number,
    timing: PropTypes.string
  }),
  triggerMouseOutEventToHideTooltip: PropTypes.shape({
    triggerMouseoutAfterXSeconds: PropTypes.number
  }),
  clickingTriggerBlinksTooltip: PropTypes.bool
});

export default Tooltip;
